import apiService from './api';
import type { OrderFilters, PaginatedOrdersResponse, OrderRecord } from '../types/orders';

class OrdersService {
  private baseUrl = '/orders';

  async getOrders(filters: OrderFilters = {}): Promise<PaginatedOrdersResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.responsible) params.append('responsible', filters.responsible);
    if (filters.status) params.append('status', filters.status);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.delivery_from) params.append('delivery_from', filters.delivery_from);
    if (filters.delivery_to) params.append('delivery_to', filters.delivery_to);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const resp = await apiService.get(`${this.baseUrl}?${params.toString()}`);

    // Normalize response shape
    if (resp.data && Array.isArray(resp.data.data) && resp.data.pagination) {
      return resp.data as PaginatedOrdersResponse;
    }

    // Fallback for very minimal backend stub
    return {
      data: resp.data.orders || resp.data.data || [],
      pagination: resp.data.pagination || {
        page: 1,
        limit: 25,
        total: (resp.data.orders || resp.data.data || []).length || 0,
        totalPages: 1
      }
    };
  }

  async updateOrder(id: number, patch: Partial<OrderRecord>): Promise<OrderRecord> {
    const resp = await apiService.patch(`${this.baseUrl}/${id}`, patch);
    return resp.data;
  }

  async createOrder(payload: Partial<OrderRecord>): Promise<OrderRecord> {
    const resp = await apiService.post(this.baseUrl, payload);
    return resp.data;
  }

  async transition(id: number, action: 'approve_drawings' | 'start_production' | 'confirm_collection' | 'mark_delivered', date?: string): Promise<OrderRecord> {
    const payload: any = { action };
    if (date) payload.date = date;
    const resp = await apiService.post(`${this.baseUrl}/${id}/transition`, payload);
    return resp.data;
  }

  async getStats(filters: OrderFilters = {}): Promise<Record<string, number>> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.responsible) params.append('responsible', filters.responsible);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.delivery_from) params.append('delivery_from', filters.delivery_from);
    if (filters.delivery_to) params.append('delivery_to', filters.delivery_to);
    const query = params.toString();
    const url = query ? `${this.baseUrl}/stats?${query}` : `${this.baseUrl}/stats`;
    const resp = await apiService.get(url);
    return resp.data;
  }

  buildExportUrl(filters: OrderFilters = {}): string {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.responsible) params.append('responsible', filters.responsible);
    if (filters.status) params.append('status', filters.status);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.delivery_from) params.append('delivery_from', filters.delivery_from);
    if (filters.delivery_to) params.append('delivery_to', filters.delivery_to);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);
    return `/orders/export?${params.toString()}`;
  }

  async setStage(id: number, stage: OrderRecord['status'], date?: string): Promise<OrderRecord> {
    const resp = await apiService.post(`${this.baseUrl}/${id}/stage`, { stage, date });
    return resp.data;
  }

  async deleteOrder(id: number): Promise<{ success: boolean }> {
    const resp = await apiService.delete(`${this.baseUrl}/${id}`);
    return resp.data;
  }

  async getReadyForTransport(): Promise<OrderRecord[]> {
    const resp = await apiService.get(`${this.baseUrl}/ready-for-transport`);
    return resp.data.data || [];
  }
}

export const ordersService = new OrdersService();
export default ordersService;
